import React, { useEffect } from 'react';
import { useBrand } from '../../../app/config/brand';
import { Icons } from '../../icons/Icons';
import { SidebarNavItem } from './SidebarNavItem';

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onPlaceOrder: () => void;
  isMobileOpen: boolean;
  onMobileToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentPage, onNavigate, isCollapsed, onToggleCollapse, onPlaceOrder, isMobileOpen, onMobileToggle }) => {
  const brand = useBrand();
  const collapsedMark = brand.logos.iconDark ?? brand.logos.icon;

  // Separate primary action from nav items
  const primaryItem = brand.navigation.sidebar.find(item => item.primary);
  const navItems = brand.navigation.sidebar.filter(item => !item.primary);

  // Group items: main nav and settings at bottom
  const mainNavItems = navItems.filter(item => item.id !== 'settings');
  const settingsItem = navItems.find(item => item.id === 'settings');

  const handleItemClick = (item: { id: string; action?: string }) => {
    if (item.action === 'modal') {
      onPlaceOrder();
    } else {
      onNavigate(item.id);
    }
    // Close mobile menu on navigation
    if (isMobileOpen && onMobileToggle) {
      onMobileToggle();
    }
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  return (
    <>
      {/* Mobile hamburger button */}
      {!isMobileOpen && (
        <button
          onClick={onMobileToggle}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg min-h-touch min-w-touch flex items-center justify-center"
          style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onMobileToggle}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 h-full flex flex-col transition-all duration-300 z-40
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={{
          width: isCollapsed ? '80px' : '256px',
          backgroundColor: brand.colors.sidebarBg
        }}
      >
        {/* Mobile close button */}
        {isMobileOpen && (
          <button
            onClick={onMobileToggle}
            className="lg:hidden absolute top-4 right-4 z-50 p-2 rounded-lg min-h-touch min-w-touch flex items-center justify-center"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: brand.colors.sidebarText }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Logo. Expanded: the brand's dark-ground logo as is, or — for a
            brand that only has light-ground art — that art on a white chip
            (Walter R32). Collapsed rail: the brand's square mark, or the
            short-name square when it has none. */}
        <div className="p-4 border-b border-white/10">
          <button
            onClick={() => handleItemClick({ id: 'dashboard' })}
            className="flex items-center w-full"
            aria-label={brand.company.name}
          >
            {isCollapsed ? (
              collapsedMark ? (
                <img src={collapsedMark} alt={brand.company.name} className="block w-10 h-10 shrink-0" />
              ) : (
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm shrink-0"
                  style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                >
                  {brand.company.shortName}
                </div>
              )
            ) : brand.logos.dark ? (
              <span data-testid="sidebar-logo-lockup" className="block">
                <img src={brand.logos.dark} alt={brand.company.name} className="block h-8 w-auto" />
              </span>
            ) : brand.logos.primary ? (
              <span
                data-testid="sidebar-logo-lockup"
                className="block w-full rounded-xl bg-white px-4 py-3 shadow-sm"
              >
                <img src={brand.logos.primary} alt={brand.company.name} className="block w-full h-auto" />
              </span>
            ) : (
              <span
                data-testid="sidebar-logo-lockup"
                className="block text-lg font-bold"
                style={{ color: brand.colors.sidebarText }}
              >
                {brand.company.name}
              </span>
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          {/* Place New Order Button */}
          {primaryItem && (
            <SidebarNavItem
              item={primaryItem}
              isActive={false}
              isCollapsed={isCollapsed}
              onClick={() => handleItemClick(primaryItem)}
            />
          )}

          {/* Main Nav Items */}
          <div className="space-y-1">
            {mainNavItems.map(item => (
              <SidebarNavItem
                key={item.id}
                item={item}
                isActive={currentPage === item.id}
                isCollapsed={isCollapsed}
                onClick={() => handleItemClick(item)}
              />
            ))}
          </div>
        </nav>

        {/* Settings & Collapse Toggle */}
        <div className="p-3 border-t border-white/10">
          {settingsItem && (
            <SidebarNavItem
              item={settingsItem}
              isActive={currentPage === 'settings'}
              isCollapsed={isCollapsed}
              onClick={() => handleItemClick(settingsItem)}
            />
          )}

          {/* Collapse Toggle - hidden on mobile */}
          <button
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden lg:flex w-full items-center justify-center gap-2 px-4 py-2 mt-2 rounded-lg transition-all hover:bg-white/10"
            style={{ color: brand.colors.sidebarText }}
          >
            {isCollapsed ? (
              <Icons.ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <Icons.ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};
